import React from "react";

import { Typography } from "@packages/ds";
import { useIntl } from "react-intl";

import Select from "ds/Select";
import { Tooltip } from "ds/Tooltip";
import { maxBackupIntervals } from "src/organization/pages/project/ManageBackupSchedulePage/util";

import IntervalNotice from "../IntervalNotice";
import IntervalNumInput from "../IntervalNumInput";

import * as S from "./BackupRow.styles";

import type {
  IntervalOptionsType,
  IntervalNumberOptionsType
} from "src/organization/pages/project/ManageBackupSchedulePage/ManageBackupSchedulePage";

type BackupRowProps = {
  rowNumber: number;
  deleteRow: () => void;
  isLastRow: boolean;
  showIncrementError: boolean;
  intervalCount?: number;
  onIntervalCountChange: (newNumber: number) => void;
  onIntervalCountBlur: (newNumber: number) => void;
  intervalNumberOptions: IntervalNumberOptionsType[];
  intervalLabel?: string;
  onIntervalLabelChange: (option) => void;
  intervalOptions: IntervalOptionsType[];
  intervalSelection?: IntervalOptionsType;
  backupsCount?: number;
  onBackupsCountChange: (newNumber: number) => void;
};
const BackupRow = ({
  rowNumber,
  deleteRow,
  isLastRow,
  showIncrementError,
  intervalCount,
  onIntervalCountChange,
  onIntervalCountBlur,
  intervalNumberOptions,
  intervalLabel,
  onIntervalLabelChange,
  intervalOptions,
  intervalSelection,
  backupsCount,
  onBackupsCountChange
}: BackupRowProps) => {
  const intl = useIntl();

  const showRemoveBtn =
    typeof intervalCount === "number" &&
    intervalLabel &&
    typeof backupsCount === "number";

  const showTooltip = typeof intervalCount === "number";

  const showInvalid =
    showIncrementError ||
    (!intervalCount && !intervalLabel && typeof backupsCount === "number");

  const inputPlaceholder = intl.formatMessage({
    id: "backup_schedule.column.placeholder",
    defaultMessage: "#"
  });

  const intervalNumberSelection = intervalNumberOptions.find(
    option => option.id === intervalCount
  );

  const intervalNumberSelect = (
    <Select
      id="interval_Number_Select"
      value={intervalNumberSelection}
      options={intervalNumberOptions}
      onChange={option =>
        onIntervalCountChange((option as IntervalNumberOptionsType).id)
      }
      isMulti={false}
      aria-invalid={showInvalid}
    />
  );

  const intervalNumberInputEl = (
    <IntervalNumInput
      id="backup_schedule-interval"
      value={intervalCount ?? ""}
      placeholder={inputPlaceholder}
      onBlur={onIntervalCountBlur}
      onChange={onIntervalCountChange}
      isValid={!showInvalid}
      width="100%"
    />
  );

  const intervalNumberInput =
    showTooltip && maxBackupIntervals[intervalLabel!] ? (
      <Tooltip
        tooltip={intl.formatMessage(
          { id: "backup_schedule.column.maximum" },
          { maxBackups: maxBackupIntervals[intervalLabel!] }
        )}
      >
        {intervalNumberInputEl}
      </Tooltip>
    ) : (
      intervalNumberInputEl
    );

  return (
    <S.Row>
      <S.RowLabelWrapper>
        <Typography.BodyText $color="granite" weight="semiBold" size="small">
          {intl.formatMessage(
            { id: "backup_schedule.row.name" },
            { number: rowNumber }
          )}
        </Typography.BodyText>
        {isLastRow && <IntervalNotice />}
      </S.RowLabelWrapper>
      <Typography.BodyText $color="granite" size="small">
        {intl.formatMessage({
          id: "backup_schedule.column.every.label",
          defaultMessage: "Take backups every"
        })}
      </Typography.BodyText>

      <S.BackupIntervalWrapper>
        {intervalLabel === "h" ? intervalNumberSelect : intervalNumberInput}

        <S.IntervalDurationSelect
          value={intervalSelection}
          options={intervalOptions}
          onChange={option => onIntervalLabelChange(option)}
          placeholder={intl.formatMessage({
            id: "backup_schedule.column.up_to.placeholder",
            defaultMessage: "Select"
          })}
          isMulti={false}
          aria-invalid={showInvalid}
        />
        {showIncrementError && (
          <S.Error>
            {intl.formatMessage({
              id: "backup_schedule.row.name.error",
              defaultMessage: "Intervals must be a multiple of one another"
            })}
          </S.Error>
        )}
      </S.BackupIntervalWrapper>
      <S.BackupsEveryText $color="granite" size="small">
        {intl.formatMessage({
          id: "backup_schedule.column.up_to.label",
          defaultMessage: "and store up to a total of"
        })}
      </S.BackupsEveryText>

      <IntervalNumInput
        id="backup_schedule-backups"
        value={backupsCount ?? ""}
        placeholder={inputPlaceholder}
        onBlur={onBackupsCountChange}
        onChange={onBackupsCountChange}
        isValid={!showInvalid}
        width="100%"
      />
      {showRemoveBtn ? (
        <S.RemoveRowBtn
          variant="link"
          analyticId="links.documentation.environments.backup"
          onClick={deleteRow}
        >
          {intl.formatMessage({
            id: "backup_schedule.btn.remove",
            defaultMessage: "Remove"
          })}
        </S.RemoveRowBtn>
      ) : (
        <span />
      )}
    </S.Row>
  );
};

export default BackupRow;
